import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function formatDate(value) {
  if (!value) return 'Filing date not recorded';
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString();
}

function formatPayment(payment) {
  if (payment === null || payment === undefined || payment === '') return 'Amount not entered';
  const amount = Number(payment);
  if (!Number.isFinite(amount)) return payment;
  return amount.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export default function WageOrderReport() {
  const [isOpen, setIsOpen] = useState(false);
  const [report, setReport] = useState({ count: 0, cases: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/firm/reports/wage-orders-needed/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || payload.error || 'Could not load the wage-order report.');
      }
      setReport({
        count: Number(payload.count) || 0,
        cases: Array.isArray(payload.cases) ? payload.cases : [],
      });
    } catch (requestError) {
      setError(requestError.message || 'Could not load the wage-order report.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cases = report.cases || [];

  return (
    <section className="overflow-hidden rounded-lg bg-white shadow">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls="wage-order-report-details"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left hover:bg-gray-50"
      >
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Wage Orders Still Needed</h2>
          <p className="mt-1 text-sm text-gray-600">
            Filed Chapter 13 cases using payroll that do not have a Motion for Order to Employer docket.
          </p>
          <span className={`mt-3 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
            error
              ? 'bg-red-100 text-red-800'
              : report.count
                ? 'bg-amber-100 text-amber-900'
                : 'bg-green-100 text-green-800'
          }`}>
            {loading ? 'Checking cases…' : error ? 'Report unavailable' : `${report.count} need filing`}
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 shrink-0 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 shrink-0 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div id="wage-order-report-details" className="border-t border-gray-200 px-6 py-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600">Oldest filing dates appear first.</p>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-200 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh report
            </button>
          </div>

          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : loading && !cases.length ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-5 text-sm text-gray-600">
              Checking filed Chapter 13 cases…
            </div>
          ) : !cases.length ? (
            <div className="rounded-md border border-green-200 bg-green-50 px-4 py-5 text-sm text-green-800">
              No filed Chapter 13 cases currently need a wage order.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-gray-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Case</th>
                    <th className="px-4 py-3 font-semibold">Filed</th>
                    <th className="px-4 py-3 font-semibold">Payroll payment</th>
                    <th className="px-4 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {cases.map((item) => (
                    <tr key={item.case_id}>
                      <td className="px-4 py-3 align-top">
                        <div className="font-semibold text-gray-900">
                          {item.case_number || item.case_id}
                        </div>
                        <div className="mt-1 text-gray-600">{item.debtor_names || 'Debtor name unavailable'}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top text-gray-700">
                        {formatDate(item.date_filed)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="space-y-2">
                          {(item.payroll_debtors || []).map((debtor) => (
                            <div key={debtor.debtor_number}>
                              <div className="font-medium text-gray-900">
                                Debtor {debtor.debtor_number}: {debtor.name}
                              </div>
                              <div className="text-gray-600">
                                {formatPayment(debtor.payment)}
                                {debtor.period_label ? ` · ${debtor.period_label}` : ' · Period not entered'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-wrap justify-end gap-2">
                          <a
                            href={`/case/${item.case_id}`}
                            className="rounded-md bg-gray-100 px-3 py-2 font-semibold text-gray-800 hover:bg-gray-200"
                          >
                            Open case
                          </a>
                          <a
                            href={`/case/${item.case_id}/forms/wage-order`}
                            className="rounded-md bg-indigo-600 px-3 py-2 font-semibold text-white hover:bg-indigo-700"
                          >
                            Prepare wage order
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
